import { ReactNode } from 'react';
import styles from './progress-bar.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProgressBarProps {
  /** Current progress value (0–100) */
  value: number;
  /** Primary label shown on the left of the header row */
  statusLabel: string;
  /** Optional icon rendered before the status label */
  statusIcon?: ReactNode;
  /** Optional badge text shown in the centre of the header row (e.g. a job ID) */
  badge?: string;
  /** Optional secondary text shown on the right of the header row */
  helperText?: string;
  /** Text shown between the header and the bar (e.g. "53% · 21 / 40 wells") */
  metaText?: string;
}

export const ProgressBar = ({
  value,
  statusLabel,
  statusIcon,
  badge,
  helperText,
  metaText,
}: ProgressBarProps) => {
  const pct = Math.min(Math.max(value, 0), 100);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.statusLabel}>
          {statusIcon && (
            <span className={styles.statusIcon}>{statusIcon}</span>
          )}
          <span>{statusLabel}</span>
        </div>

        {badge && <span className={styles.badge}>{badge}</span>}

        {helperText && <span className={styles.helperText}>{helperText}</span>}
      </div>

      {metaText && <div className={styles.meta}>{metaText}</div>}

      <div
        className={styles.track}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={statusLabel}
      >
        <div className={styles.fill} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
